import { Fragment, ReactNode, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { ADMIN_ALL_USER_PLANS_QUERY, ADMIN_COST_FORECAST_QUERY, SHARED_COST_FORECAST_QUERY } from '../../graphql/queries';
import { CdnPlanOption, fmtCount, fmtGb, planShare } from '../../utils/cdnPlan';
import CalculatorShareLinks from './CalculatorShareLinks';
import CdnPlanTable from './CdnPlanTable';
import IntakePattern, { DEFAULT_INTAKE, IntakeState, intakeInput } from './IntakePattern';
import styles from './AdminCostCalculator.module.css';

type LineType = 'point_cloud' | 'two_d' | 'three_d' | 'gaussian' | 'orthomosaic' | 'video360' | 'document';
type Unit = 'MB' | 'GB';

interface Line {
  id: string;
  type: LineType;
  files: number;
  size: number;
  unit: Unit;
  conversionsPerFile: number;
  downloadsPerFile: number;
  viewsPerFile: number;
  lane: string;
}

interface Step {
  label: string; lane: string; jobs: number; runHours: number;
  computeUsd: number; hoursPerJob: number; samples: number; source: string;
  machineHours: number; costSource: string;
}
interface LaneModel {
  lane: string; samples: number; machines: number; startupHours: number;
  busyPerRunHour: number; jobsPerStart: number; source: string;
}
interface StorageMultiplier { type: string; label: string; multiplier: number }
interface ResultLine {
  id: string; type: string; label: string; files: number; avgSizeMb: number;
  conversionsPerFile: number; lane: string; totalGb: number; jobs: number; runHours: number;
  computeUsd: number; storageFactor: number; storageMonthlyUsd: number; storageUsd: number; viewDelivery: string;
  downloadDelivery: string; downloads: number; downloadGb: number; downloadUsd: number;
  views: number; viewGb: number; viewUsd: number; transferUsd: number;
  totalUsd: number; firstMonthUsd: number; avgMonthlyUsd: number; perFileUsd: number; steps: Step[];
}
interface RangeTotals {
  totalUsd: number; firstMonthUsd: number; lastMonthUsd: number; avgMonthlyUsd: number;
  perUserUsd: number; perUserMonthlyUsd: number;
}
interface ForecastRange { quietPct: number; low: RangeTotals; high: RangeTotals; monthlyLow: number[]; monthlyHigh: number[] }

interface ForecastMonth {
  month: number; newFiles: number; storedFiles: number; storedGb: number;
  computeUsd: number; downloadUsd: number; storageUsd: number;
  storageHotGb: number; storageArchiveGb: number;
  storageHotUsd: number; storageArchiveUsd: number; storageSavedUsd: number;
  storageFrequentGb: number; storageInfrequentGb: number; storageInstantGb: number; cdnDownloadGb: number;
  views: number; viewRequests: number; viewGb: number;
  viewUsd: number; fixedUsd: number; totalUsd: number;
}
interface ForecastDelivery {
  intelligentTiering: boolean; downloadsViaCloudfront: boolean; maxCdnDownloadGb: number;
  originalsReadPct: number; tieringSavedUsd: number; monitoringUsd: number;
}
interface Model {
  key: string; label: string; lane: string; samples: number;
  overheadHours: number; hoursPerGb: number; medianGb: number; source: string;
}

const TYPES: { value: LineType; label: string; hint: string }[] = [
  { value: 'point_cloud', label: 'Point cloud', hint: 'E57 / LAS / LAZ / RCP → Potree' },
  { value: 'gaussian', label: 'Gaussian splat', hint: 'point-cloud conversion + GPU training' },
  { value: 'two_d', label: '2D drawing', hint: 'DWG / DXF → SVG' },
  { value: 'three_d', label: '3D model', hint: 'IFC / OBJ / STL → GLB' },
  { value: 'orthomosaic', label: 'Orthomosaic', hint: 'GeoTIFF → cloud-optimised GeoTIFF' },
  { value: 'video360', label: '360 video', hint: 'MP4 / MOV → streaming renditions' },
  { value: 'document', label: 'Document', hint: 'PDF and friends — stored, never converted' },
];

const LANES = ['auto', 'heavy', 'standard', 'light'];

const PRESETS: { label: string; hint: string; lines: Omit<Line, 'id'>[]; months: number }[] = [
  {
    label: 'Single surveyor',
    hint: 'one small firm: a few scans, drawings, documents and models a month',
    months: 12,
    lines: [
      { type: 'point_cloud', files: 4, size: 9, unit: 'GB', conversionsPerFile: 1, downloadsPerFile: 0.5, viewsPerFile: 8, lane: 'auto' },
      { type: 'two_d', files: 20, size: 120, unit: 'MB', conversionsPerFile: 1, downloadsPerFile: 0.5, viewsPerFile: 3, lane: 'auto' },
      { type: 'document', files: 30, size: 100, unit: 'MB', conversionsPerFile: 1, downloadsPerFile: 2, viewsPerFile: 3, lane: 'auto' },
      { type: 'three_d', files: 8, size: 130, unit: 'MB', conversionsPerFile: 1, downloadsPerFile: 0.5, viewsPerFile: 3, lane: 'auto' },
    ],
  },
  {
    label: 'Mid-size customer',
    hint: 'a project team scanning weekly',
    months: 12,
    lines: [
      { type: 'point_cloud', files: 40, size: 16, unit: 'GB', conversionsPerFile: 1, downloadsPerFile: 0.5, viewsPerFile: 3, lane: 'auto' },
      { type: 'three_d', files: 15, size: 120, unit: 'MB', conversionsPerFile: 1, downloadsPerFile: 0.5, viewsPerFile: 3, lane: 'auto' },
      { type: 'two_d', files: 80, size: 6, unit: 'MB', conversionsPerFile: 1, downloadsPerFile: 0.5, viewsPerFile: 3, lane: 'auto' },
      { type: 'gaussian', files: 1, size: 6, unit: 'GB', conversionsPerFile: 1, downloadsPerFile: 0.5, viewsPerFile: 3, lane: 'auto' },
      { type: 'video360', files: 3, size: 5, unit: 'GB', conversionsPerFile: 1, downloadsPerFile: 0.5, viewsPerFile: 3, lane: 'auto' },
      { type: 'document', files: 100, size: 120, unit: 'MB', conversionsPerFile: 1, downloadsPerFile: 0.5, viewsPerFile: 3, lane: 'auto' },
    ],
  },
  {
    label: 'Enterprise',
    hint: 'large scans, splats, orthomosaics and 360 videos, downloaded by many',
    months: 12,
    lines: [
      { type: 'point_cloud', files: 120, size: 25, unit: 'GB', conversionsPerFile: 1, downloadsPerFile: 0.25, viewsPerFile: 3, lane: 'auto' },
      { type: 'gaussian', files: 24, size: 12, unit: 'GB', conversionsPerFile: 1, downloadsPerFile: 0.42, viewsPerFile: 3, lane: 'auto' },
      { type: 'three_d', files: 60, size: 200, unit: 'MB', conversionsPerFile: 1, downloadsPerFile: 0.67, viewsPerFile: 3, lane: 'auto' },
      { type: 'two_d', files: 600, size: 8, unit: 'MB', conversionsPerFile: 1, downloadsPerFile: 0.25, viewsPerFile: 3, lane: 'auto' },
      { type: 'orthomosaic', files: 20, size: 5, unit: 'GB', conversionsPerFile: 1, downloadsPerFile: 0.5, viewsPerFile: 3, lane: 'auto' },
      { type: 'video360', files: 30, size: 5, unit: 'GB', conversionsPerFile: 1, downloadsPerFile: 0.5, viewsPerFile: 5, lane: 'auto' },
    ],
  },
];

const usd = (n: number) =>
  `$${(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const usdFine = (n: number) => (Math.abs(n ?? 0) < 1 ? `$${(n ?? 0).toFixed(4)}` : usd(n));
const num = (n: number, dp = 1) => (n ?? 0).toLocaleString('en-US', { maximumFractionDigits: dp });
const pct = (share: number) => `${Math.round((share ?? 0) * 100)}%`;

// Why a conversion step's price is still a fallback, or null when both parts are measured.
export const notMeasuredReason = (s: { source: string; costSource: string }) => {
  const runtime = s.source === 'default';
  const machine = s.costSource !== 'machine';
  if (runtime && machine) return 'Run time is a built-in estimate (no finished jobs of this kind yet) and machine time is the per-job lane rate (too few jobs on terminated machines in this lane).';
  if (runtime) return 'Run time is a built-in estimate: no finished jobs of this kind yet.';
  if (machine) return 'Machine time is the per-job lane rate: too few jobs on terminated machines in this lane yet.';
  return null;
};

// ── Storage-by-tier chart ─────────────────────────────────────────────────────
// Stacked monthly columns: S3 Standard below, the archive tier above. Same
// validated palette slots as the analysis charts — storage keeps its orange,
// the cold tier takes blue.
const TIER_HOT_COLOR = '#eb6834';
const TIER_ARCHIVE_COLOR = '#2a78d6';

const niceMax = (v: number) => {
  if (v <= 0) return 1;
  const pow = 10 ** Math.floor(Math.log10(v));
  const n = v / pow;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * pow;
};

// Rounded top corners only — the flat bottom sits on the segment below.
const capPath = (x: number, y: number, w: number, h: number, r: number) => {
  const rr = Math.min(r, w / 2, h);
  return `M${x} ${y + h} L${x} ${y + rr} Q${x} ${y} ${x + rr} ${y} L${x + w - rr} ${y} `
    + `Q${x + w} ${y} ${x + w} ${y + rr} L${x + w} ${y + h} Z`;
};

const TierChart = ({ title, rows, hotOf, archiveOf, fmt }: {
  title: string;
  rows: ForecastMonth[];
  hotOf: (m: ForecastMonth) => number;
  archiveOf: (m: ForecastMonth) => number;
  fmt: (v: number) => string;
}) => {
  const [hover, setHover] = useState<number | null>(null);
  const W = 380;
  const H = 190;
  const padL = 48;
  const padR = 8;
  const padT = 10;
  const padB = 22;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const n = rows.length;
  const max = niceMax(Math.max(0.0001, ...rows.map((r) => hotOf(r) + archiveOf(r))));
  const slot = plotW / Math.max(1, n);
  const barW = Math.min(24, Math.max(3, slot - 2));
  const yOf = (v: number) => padT + plotH * (1 - v / max);
  const labelEvery = Math.max(1, Math.ceil(n / 8));
  const ticks = [0.5, 1].map((f) => max * f);

  return (
    <div className={styles.tierCard}>
      <p className={styles.tierTitle}>
        {title}
        <span className={styles.tierLegend}>
          <span><span className={styles.tierDot} style={{ background: TIER_HOT_COLOR }} />Standard</span>
          <span><span className={styles.tierDot} style={{ background: TIER_ARCHIVE_COLOR }} />Archive</span>
        </span>
      </p>
      <svg className={styles.tierSvg} viewBox={`0 0 ${W} ${H}`} onMouseLeave={() => setHover(null)}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padL} x2={W - padR} y1={yOf(t)} y2={yOf(t)} stroke="#f3f4f6" strokeWidth="1" />
            <text x={padL - 6} y={yOf(t) + 3} textAnchor="end" fontSize="9" fill="#9ca3af">{fmt(t)}</text>
          </g>
        ))}
        <line x1={padL} x2={W - padR} y1={padT + plotH} y2={padT + plotH} stroke="#e5e7eb" strokeWidth="1" />
        {rows.map((r, i) => {
          const hot = hotOf(r);
          const archive = archiveOf(r);
          const x = padL + i * slot + (slot - barW) / 2;
          const hHot = (hot / max) * plotH;
          const hArch = (archive / max) * plotH;
          const gap = hHot > 0 && hArch > 0 ? 2 : 0;
          const hotY = padT + plotH - hHot;
          const archY = hotY - gap - hArch;
          const dim = hover !== null && hover !== i ? 0.45 : 1;
          return (
            <g key={r.month} opacity={dim}>
              {hArch > 0
                ? <path d={capPath(x, archY, barW, hArch, 4)} fill={TIER_ARCHIVE_COLOR} />
                : null}
              {hHot > 0 && (hArch > 0
                ? <rect x={x} y={hotY} width={barW} height={hHot} fill={TIER_HOT_COLOR} />
                : <path d={capPath(x, hotY, barW, hHot, 4)} fill={TIER_HOT_COLOR} />)}
              {(i + 1) % labelEvery === 0 && (
                <text x={x + barW / 2} y={H - 8} textAnchor="middle" fontSize="9" fill="#9ca3af">
                  M{r.month}
                </text>
              )}
              <rect
                x={padL + i * slot} y={padT} width={slot} height={plotH}
                fill="transparent" onMouseEnter={() => setHover(i)}
              />
            </g>
          );
        })}
      </svg>
      {hover !== null && rows[hover] && (
        <div
          className={styles.tierTip}
          style={{ left: `${Math.min(86, Math.max(14, ((padL + hover * slot + slot / 2) / W) * 100))}%` }}
        >
          <div className={styles.tierTipTitle}>Month {rows[hover].month}</div>
          <div>
            <span className={styles.tierDot} style={{ background: TIER_HOT_COLOR }} />
            Standard {fmt(hotOf(rows[hover]))}
          </div>
          <div>
            <span className={styles.tierDot} style={{ background: TIER_ARCHIVE_COLOR }} />
            Archive {fmt(archiveOf(rows[hover]))}
          </div>
        </div>
      )}
    </div>
  );
};

let seq = 0;
const BIG_TYPES: LineType[] = ['point_cloud', 'gaussian', 'video360'];
const newLine = (type: LineType = 'point_cloud'): Line => ({
  id: `l${++seq}`, type, files: 10, size: BIG_TYPES.includes(type) ? 5 : 20,
  unit: BIG_TYPES.includes(type) ? 'GB' : 'MB',
  conversionsPerFile: 1, downloadsPerFile: 0.5, viewsPerFile: 3, lane: 'auto',
});

const PlainShell = ({ children }: { children: ReactNode }) => <>{children}</>;

// A share link renders the same calculator outside the admin layout; the
// server leaves out how many real jobs and sessions the models came from.
const AdminCostCalculator = ({ shareToken }: { shareToken?: string } = {}) => {
  const shared = !!shareToken;
  const Shell = shared ? PlainShell : DashboardLayout;
  const [lines, setLines] = useState<Line[]>(() => PRESETS[1].lines.map((l) => ({ ...l, id: `l${++seq}` })));
  const [months, setMonths] = useState(12);
  const [users, setUsers] = useState(1);
  // A scenario is one customer's slice, so the default is a token $1 rather than
  // the platform's real always-on bill (that lives in Cost Analysis).
  const [fixedMonthly, setFixedMonthly] = useState(1);
  // '' = the plan set in Cost Analysis → Unit prices.
  const [cdnPlan, setCdnPlan] = useState('');
  // Old projects are rarely reopened: each month a file is opened this much less.
  const [viewDecay, setViewDecay] = useState(30);
  // Intelligent-Tiering only: how many stored originals someone reads again in a month.
  const [originalsRead, setOriginalsRead] = useState(0);
  const [intake, setIntake] = useState<IntakeState>(DEFAULT_INTAKE);

  // The scenario changes on every keystroke; the server only needs the settled value.
  const scenario = useMemo(() => ({
    lines: lines.map((l) => ({
      id: l.id,
      type: l.type,
      files: Math.max(0, Math.round(l.files || 0)),
      avgSizeMb: Math.max(0, (l.size || 0) * (l.unit === 'GB' ? 1024 : 1)),
      conversionsPerFile: Math.max(0, l.conversionsPerFile || 0),
      downloadsPerFile: Math.max(0, l.downloadsPerFile || 0),
      viewsPerFile: Math.max(0, l.viewsPerFile || 0),
      lane: l.lane,
    })),
    months,
    users: Math.max(0, Math.round(users || 0)),
    fixedMonthlyUsd: Math.max(0, fixedMonthly || 0),
    cdnPlan: cdnPlan || null,
    viewDecayPct: Math.min(100, Math.max(0, viewDecay || 0)),
    originalsReadPct: Math.min(100, Math.max(0, originalsRead || 0)),
    intake: intakeInput(intake),
  }), [lines, months, users, fixedMonthly, cdnPlan, viewDecay, originalsRead, intake]);

  const [settled, setSettled] = useState(scenario);
  useEffect(() => {
    const t = setTimeout(() => setSettled(scenario), 350);
    return () => clearTimeout(t);
  }, [scenario]);

  const { data, loading, error } = useQuery(shared ? SHARED_COST_FORECAST_QUERY : ADMIN_COST_FORECAST_QUERY, {
    variables: shared ? { token: shareToken, input: settled } : { input: settled },
    fetchPolicy: 'cache-and-network',
  });
  const forecast = shared ? data?.sharedCostForecast : data?.adminCostForecast;
  const totals = forecast?.totals;
  const cdn = forecast?.cdn;
  const cdnOptions = (cdn?.options ?? []) as CdnPlanOption[];
  const delivery = forecast?.delivery as ForecastDelivery | undefined;
  const ratesPlanLabel = cdnOptions.find((o) => o.id === forecast?.rates.cdnPlan)?.label;
  const range = (forecast?.range ?? null) as ForecastRange | null;
  const monthlyRows = (forecast?.monthly ?? []) as ForecastMonth[];
  const firstRow = monthlyRows[0];
  const lastRow = monthlyRows[monthlyRows.length - 1];
  const rangeText = (low: number, high: number) => `range ${usd(low)} – ${usd(high)}`;

  // Plan quotas as one-click storage limits; share-link visitors type their own.
  const { data: plansData } = useQuery(ADMIN_ALL_USER_PLANS_QUERY, { skip: shared || intake.pattern !== 'capped' });
  const planLimits = ((plansData?.adminAllUserPlans ?? []) as { name: string; accountStorage: number }[])
    .map((pl) => ({ name: pl.name, gb: Number(pl.accountStorage) / 1024 ** 3 }))
    .filter((pl) => pl.gb > 0)
    .sort((a, b) => a.gb - b.gb);

  const patch = (id: string, changes: Partial<Line>) =>
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, ...changes } : l)));

  const applyPreset = (p: typeof PRESETS[number]) => {
    setLines(p.lines.map((l) => ({ ...l, id: `l${++seq}` })));
    // A preset describes one customer; how many users share it is the admin's call.
    setUsers(1);
    setMonths(p.months);
  };

  const resultById = new Map<string, ResultLine>(
    ((forecast?.lines ?? []) as ResultLine[]).map((l) => [l.id, l]),
  );

  return (
    <Shell>
      <div className="space-y-6">
        <div className="flex justify-between items-end flex-wrap gap-3">
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-on-surface font-headline">Cost Calculator</h2>
            <p className="text-on-surface-variant mt-1">
              Describe what arrives every month; files accumulate, so each month stores everything uploaded so far.
            </p>
          </div>
        </div>

        {/* ── Scenario ── */}
        <div className={styles.panel}>
          <div className={styles.panelHead}>
            <h3 className={styles.panelTitle}>Expected workload</h3>
            <div className={styles.presets}>
              <span className={styles.presetLabel}>Start from</span>
              {PRESETS.map((p) => (
                <button key={p.label} className={styles.presetBtn} onClick={() => applyPreset(p)} title={p.hint}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>File type</th>
                  <th>Files / month</th>
                  <th>Average size</th>
                  <th>Conversions per file</th>
                  <th>Downloads per new file</th>
                  <th>Viewer opens / file / month</th>
                  <th>Lane</th>
                  <th title="What this line costs a month: the average month over the simulated period, and its first month below">Estimated cost / month</th>
                  <th aria-label="Remove" />
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => {
                  const r = resultById.get(l.id);
                  return (
                    <tr key={l.id}>
                      <td>
                        <select
                          className={styles.select}
                          value={l.type}
                          onChange={(e) => patch(l.id, { type: e.target.value as LineType })}
                        >
                          {TYPES.map((t) => (
                            <option key={t.value} value={t.value} title={t.hint}>{t.label}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          className={styles.input}
                          type="number"
                          min={0}
                          value={l.files}
                          onChange={(e) => patch(l.id, { files: Number(e.target.value) })}
                        />
                      </td>
                      <td>
                        <div className={styles.sizeCell}>
                          <input
                            className={styles.input}
                            type="number"
                            min={0}
                            step="any"
                            value={l.size}
                            onChange={(e) => patch(l.id, { size: Number(e.target.value) })}
                          />
                          <select
                            className={styles.unitSelect}
                            value={l.unit}
                            onChange={(e) => patch(l.id, { unit: e.target.value as Unit })}
                          >
                            <option value="MB">MB</option>
                            <option value="GB">GB</option>
                          </select>
                        </div>
                      </td>
                      <td>
                        {l.type === 'document' ? (
                          <span className={styles.muted}>—</span>
                        ) : (
                          <input
                            className={styles.input}
                            type="number"
                            min={0}
                            step="0.5"
                            value={l.conversionsPerFile}
                            onChange={(e) => patch(l.id, { conversionsPerFile: Number(e.target.value) })}
                          />
                        )}
                      </td>
                      <td>
                        <input
                          className={styles.input}
                          type="number"
                          min={0}
                          step="0.5"
                          value={l.downloadsPerFile}
                          onChange={(e) => patch(l.id, { downloadsPerFile: Number(e.target.value) })}
                        />
                      </td>
                      <td>
                        <input
                          className={styles.input}
                          type="number"
                          min={0}
                          value={l.viewsPerFile}
                          onChange={(e) => patch(l.id, { viewsPerFile: Number(e.target.value) })}
                        />
                      </td>
                      <td>
                        {l.type === 'document' ? (
                          <span className={styles.muted}>none</span>
                        ) : (
                          <select
                            className={styles.select}
                            value={l.lane}
                            onChange={(e) => patch(l.id, { lane: e.target.value })}
                          >
                            {LANES.map((x) => (
                              <option key={x} value={x}>
                                {x === 'auto' ? `auto${r ? ` (${r.lane})` : ''}` : x}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className={styles.strong}>
                        {r ? usd(r.avgMonthlyUsd) : '—'}
                        {r && <span className={styles.sub}>month 1: {usd(r.firstMonthUsd)}</span>}
                      </td>
                      <td>
                        <button
                          className={styles.iconBtn}
                          onClick={() => setLines((prev) => prev.filter((x) => x.id !== l.id))}
                          title="Remove this row"
                        >
                          <span className="material-symbols-outlined text-base">close</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {lines.length === 0 && (
                  <tr><td colSpan={9} className={styles.empty}>Add a row to start.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className={styles.controls}>
            <button className={styles.addBtn} onClick={() => setLines((prev) => [...prev, newLine()])}>
              <span className="material-symbols-outlined text-sm">add</span>
              Add file type
            </button>
            <label className={styles.field} title="Every month a new batch arrives as set under How uploads arrive; nothing is deleted unless Delete files after is set.">
              <span>Simulate</span>
              <input
                className={styles.input}
                type="number"
                min={1}
                max={120}
                value={months}
                onChange={(e) => setMonths(Number(e.target.value))}
              />
              <span className={styles.suffix}>months</span>
            </label>
            <label className={styles.field}>
              <span>Split across</span>
              <input
                className={styles.input}
                type="number"
                min={0}
                value={users}
                onChange={(e) => setUsers(Number(e.target.value))}
              />
              <span className={styles.suffix}>users</span>
            </label>
            <label className={styles.field} title="Always-on cost charged to this scenario — app servers, EBS. Set 0 to leave it out.">
              <span>Fixed platform cost</span>
              <input
                className={styles.input}
                type="number"
                min={0}
                step="1"
                value={fixedMonthly}
                onChange={(e) => setFixedMonthly(Number(e.target.value))}
              />
              <span className={styles.suffix}>$ / month</span>
            </label>
            <label className={styles.field} title="Each month a file is opened this much less than the month before; a file archived in full is not opened at all.">
              <span>Opens fall by</span>
              <input
                className={styles.input}
                type="number"
                min={0}
                max={100}
                step="5"
                value={viewDecay}
                onChange={(e) => setViewDecay(Number(e.target.value))}
              />
              <span className={styles.suffix}>% a month</span>
            </label>
            {delivery?.intelligentTiering && (
              <label className={styles.field} title="Intelligent-Tiering: an original read again (re-converted, downloaded later) goes back to Frequent Access for a month. 0 = originals are only read in their upload month.">
                <span>Originals read again</span>
                <input
                  className={styles.input}
                  type="number"
                  min={0}
                  max={100}
                  step="1"
                  value={originalsRead}
                  onChange={(e) => setOriginalsRead(Number(e.target.value))}
                />
                <span className={styles.suffix}>% of stored a month</span>
              </label>
            )}
            <label className={styles.field} title="Viewer traffic pays its share of this plan's price, by how much of the allowance it uses.">
              <span>CloudFront plan</span>
              <select className={styles.select} value={cdnPlan} onChange={(e) => setCdnPlan(e.target.value)}>
                <option value="">As in Unit prices{ratesPlanLabel ? ` (${ratesPlanLabel})` : ''}</option>
                {cdnOptions.map((o) => (
                  <option key={o.id} value={o.id}>{o.label}</option>
                ))}
              </select>
            </label>
          </div>

          <IntakePattern
            value={intake}
            onChange={setIntake}
            weights={forecast?.intake?.weights}
            quietPct={range?.quietPct ?? null}
            plans={planLimits}
          />
        </div>

        {error && !loading && (
          <p className={styles.error} role="alert">
            <span className="material-symbols-outlined text-base">error</span>
            The estimate could not be calculated: {error.message}
          </p>
        )}

        {!forecast && loading ? (
          <div className="flex items-center justify-center py-10 text-on-surface-variant">
            <span className="material-symbols-outlined animate-spin mr-2">refresh</span>
            Estimating…
          </div>
        ) : !forecast ? null : (
          <>
            {/* ── Headline ── */}
            <div className={styles.statGrid}>
              <div className={styles.statCard}>
                <p className={styles.statLabel}>First month</p>
                <p className={styles.statValue}>{usd(totals.firstMonthUsd)}</p>
                <p className={styles.statHint}>
                  {num(firstRow?.newFiles ?? 0, 0)} files convert · {num(firstRow?.storedGb ?? 0)} GB stored
                  {range && <span className={styles.range}>{rangeText(range.low.firstMonthUsd, range.high.firstMonthUsd)}</span>}
                </p>
              </div>
              <div className={styles.statCard}>
                <p className={styles.statLabel}>Month {num(forecast.months, 0)} (run rate)</p>
                <p className={styles.statValue}>{usd(totals.lastMonthUsd)}</p>
                <p className={styles.statHint}>
                  {forecast.intake?.pattern === 'constant'
                    ? `same intake, but storing ${num(lastRow?.storedFiles ?? 0, 0)} files by then`
                    : `${num(lastRow?.newFiles ?? 0, 0)} new files that month, ${num(lastRow?.storedFiles ?? 0, 0)} stored by then`}
                  {range && <span className={styles.range}>{rangeText(range.low.lastMonthUsd, range.high.lastMonthUsd)}</span>}
                </p>
              </div>
              <div className={styles.statCardAccent}>
                <p className={styles.statLabel}>Total over {num(forecast.months, 0)} months</p>
                <p className={styles.statValue}>{usd(totals.totalUsd)}</p>
                <p className={styles.statHint}>
                  convert {usd(totals.computeUsd)} + store {usd(totals.storageUsd)} + transfer {usd(totals.transferUsd)}
                  {totals.fixedUsd > 0 ? ` + fixed ${usd(totals.fixedUsd)}` : ''}
                  {range && <span className={styles.range}>{rangeText(range.low.totalUsd, range.high.totalUsd)}</span>}
                </p>
              </div>
              <div className={styles.statCard}>
                <p className={styles.statLabel}>Per user</p>
                <p className={styles.statValue}>{forecast.users ? usd(totals.perUserUsd) : '—'}</p>
                <p className={styles.statHint}>
                  {forecast.users
                    ? `${usd(totals.perUserMonthlyUsd)} per user per month on average`
                    : 'set a user count to split it'}
                  {range && forecast.users > 0 && <span className={styles.range}>{rangeText(range.low.perUserUsd, range.high.perUserUsd)}</span>}
                </p>
              </div>
            </div>

            <div className={styles.statGrid}>
              <div className={styles.statCard}>
                <p className={styles.statLabel}>Conversion compute</p>
                <p className={styles.statValue}>{usd(totals.computeUsd)}</p>
                <p className={styles.statHint}>billed in each upload month, {usd(totals.monthlyComputeUsd)}/month</p>
              </div>
              <div className={styles.statCard}>
                <p className={styles.statLabel}>Data transfer</p>
                <p className={styles.statValue}>{usd(totals.transferUsd)}</p>
                <p className={styles.statHint}>
                  {num(totals.downloads, 0)} downloads ({num(totals.downloadGb)} GB) ·
                  {' '}{num(totals.views, 0)} viewer opens ({num(totals.viewGb)} GB)
                </p>
              </div>
              <div className={styles.statCard}>
                <p className={styles.statLabel}>Storage for the period</p>
                <p className={styles.statValue}>{usd(totals.storageUsd)}</p>
                <p className={styles.statHint}>
                  {forecast.archive?.enabled
                    ? `archive tiering saves ${usd(forecast.archive.savedUsd)} vs all-hot`
                    : 'uploads × each type\'s storage factor'}
                </p>
              </div>
            </div>

            {/* ── Month by month ── */}
            <div className={styles.panel}>
              <h3 className={styles.panelTitle}>Month by month</h3>
              <p className={styles.note}>
                Conversion and deliverable downloads repeat with every new batch; storage and viewer
                traffic bill on everything accumulated so far, so the same intake costs a little more
                each month.
                {forecast.archive?.enabled && (
                  <>
                    {' '}Archive tiering is on: each batch's originals move to the cold tier after{' '}
                    {num(forecast.archive.stage1Months, 0)} months and everything after{' '}
                    {num(forecast.archive.stage2Months, 0)} months (when it also stops generating
                    viewer traffic) — the storage column flattens instead of growing forever.
                  </>
                )}
              </p>
              <div className={styles.tableScroll}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>New files</th>
                      <th>Stored files</th>
                      <th>Stored GB</th>
                      <th>Conversion</th>
                      <th>Downloads</th>
                      <th>Storage</th>
                      <th>Viewer</th>
                      <th>Fixed</th>
                      <th>Total</th>
                      {range && <th>Quiet – busy</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyRows.map((m, idx) => (
                      <tr key={m.month}>
                        <td className={styles.strong}>{m.month}</td>
                        <td>{num(m.newFiles, 0)}</td>
                        <td>{num(m.storedFiles, 0)}</td>
                        <td>{num(m.storedGb)}</td>
                        <td>{usd(m.computeUsd)}</td>
                        <td>{usd(m.downloadUsd)}</td>
                        <td>{usd(m.storageUsd)}</td>
                        <td>
                          {usd(m.viewUsd)}
                          {m.views > 0 && <span className={styles.sub}>{fmtCount(m.viewRequests)} req</span>}
                        </td>
                        <td>{usd(m.fixedUsd)}</td>
                        <td className={styles.strong}>{usd(m.totalUsd)}</td>
                        {range && <td className={styles.muted}>{usd(range.monthlyLow[idx] ?? 0)} – {usd(range.monthlyHigh[idx] ?? 0)}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── CloudFront ── */}
            <div className={styles.panel}>
              <h3 className={styles.panelTitle}>CloudFront</h3>
              <p className={styles.note}>
                Every viewer streams through CloudFront: an open costs{' '}
                {num(forecast.rates.viewerRequestsPerView, 0)} requests and {num(forecast.rates.viewerGbPerView, 2)} GB
                for a point cloud, {num(forecast.rates.splatRequestsPerView, 0)} and{' '}
                {num(forecast.rates.splatGbPerView, 2)} GB for a splat, {num(forecast.rates.video360RequestsPerView, 0)} and{' '}
                {num(forecast.rates.video360GbPerView, 2)} GB for a 360 video,{' '}
                {num(forecast.rates.orthoRequestsPerView, 0)} and {num(forecast.rates.orthoGbPerView, 2)} GB for an
                orthomosaic. Downloads up to 50 GB use the same plan when they go through CloudFront;
                documents open from S3 and pay egress. A scenario is a
                slice of the platform&apos;s traffic, so it pays the part of the {cdn.pricedPlanLabel} plan
                its opens use{cdn.perViewUsd > 0 && ` (${usdFine(cdn.perViewUsd)} per open on average)`}.
                {cdn.plan === 'payg' && ' On pay-as-you-go that is the per-GB and per-request price; the free tier belongs to the whole account.'}
                {' '}Opens fall by {num(forecast.viewDecayPct, 0)}% a month as files age, so the plans
                are compared on the busiest month (month {num(cdn.peakMonth, 0)}), as if this scenario
                were all the traffic the distribution serves.
                {!shared && forecast.viewerMeasured.sessions > 0 && (
                  ` For comparison, ${num(forecast.viewerMeasured.sessions, 0)} reported sessions in the last 90 days averaged ${num(forecast.viewerMeasured.requests, 0)} requests, ${num(forecast.viewerMeasured.gb, 2)} GB and ${num(forecast.viewerMeasured.minutes, 1)} minutes. Those are today's viewers, who may not behave like the customers in this scenario, so the per-session figures stay in Unit prices.`
                )}
              </p>
              <div className={`${styles.statGrid} ${styles.cdnStats}`}>
                <div className={styles.statCard}>
                  <p className={styles.statLabel}>CloudFront opens in month {num(cdn.peakMonth, 0)}</p>
                  <p className={styles.statValue}>{num(cdn.peakViews, 0)}</p>
                  <p className={styles.statHint}>{fmtCount(cdn.peakRequests)} requests · {fmtGb(cdn.peakGb)}</p>
                </div>
                <div className={styles.statCard}>
                  <p className={styles.statLabel}>{cdn.planLabel} allowance used</p>
                  <p className={styles.statValue}>{cdn.plan === 'payg' ? '—' : pct(planShare(cdn))}</p>
                  <p className={styles.statHint}>
                    {cdn.plan === 'payg'
                      ? 'pay-as-you-go has no allowance'
                      : `requests ${pct(cdn.requestShare)} · data ${pct(cdn.gbShare)}`}
                  </p>
                </div>
                <div className={styles.statCard}>
                  <p className={styles.statLabel}>Viewer cost for the period</p>
                  <p className={styles.statValue}>{usd(totals.viewUsd)}</p>
                  <p className={styles.statHint}>{num(totals.views, 0)} opens over {num(forecast.months, 0)} months</p>
                </div>
                <div className={styles.statCard}>
                  <p className={styles.statLabel}>Plan this traffic needs</p>
                  <p className={styles.statValue}>{cdn.recommendedLabel}</p>
                  <p className={styles.statHint}>cheapest with 20% to spare in month {num(cdn.peakMonth, 0)}</p>
                </div>
              </div>
              {cdn.firstOverMonth > 0 && (
                <p className={styles.warn}>
                  From month {cdn.firstOverMonth} this scenario alone passes the {cdn.planLabel}{' '}
                  allowance, so the platform could not stay on that plan:{' '}
                  {cdn.recommendedLabel} fits it.
                  {cdn.pricedPlan !== cdn.plan && (cdn.pricedPlan === 'payg'
                    ? ' Its CloudFront traffic is priced pay-as-you-go, since no flat plan holds it.'
                    : ` Its CloudFront traffic is priced on ${cdn.pricedPlanLabel}, not as a share of ${cdn.planLabel}.`)}
                </p>
              )}
              <CdnPlanTable options={cdnOptions} current={cdn.plan} recommended={cdn.recommended} />
            </div>

            {/* ── Storage by tier ── */}
            <div className={styles.panel}>
              <h3 className={styles.panelTitle}>Storage by tier</h3>
              <p className={styles.note}>
                {delivery?.intelligentTiering && (
                  <>
                    The bucket runs Intelligent-Tiering: a batch is in Frequent Access
                    ({usdFine(forecast.rates.s3UsdPerGbMonth)}/GB-month) in a month someone opens it,
                    Infrequent ({usdFine(forecast.rates.s3InfrequentUsdPerGbMonth)}) after one or two
                    months unread, Archive Instant ({usdFine(forecast.rates.s3InstantArchiveUsdPerGbMonth)})
                    after three — the expected share from its opens, which fall as it ages; originals
                    are only read in their upload month
                    {delivery.originalsReadPct > 0 && `, then ${num(delivery.originalsReadPct, 0)}% of them a month`}.
                    That saves {usd(delivery.tieringSavedUsd)} over the period, after{' '}
                    {usd(delivery.monitoringUsd)} of monitoring.{' '}
                  </>
                )}
                {forecast.archive?.enabled ? (
                  <>
                    {!delivery?.intelligentTiering && <>Each monthly batch starts in S3 Standard ({usdFine(forecast.rates.s3UsdPerGbMonth)}/GB-month). </>}
                    Its originals move to the archive tier ({usdFine(forecast.rates.s3ArchiveUsdPerGbMonth)}/GB-month)
                    after {num(forecast.archive.stage1Months, 0)} months
                    {forecast.archive.stage2Months > 0
                      ? <>, and its viewer assets after {num(forecast.archive.stage2Months, 0)} months.</>
                      : <>; viewer assets stay in S3 for good.</>}
                  </>
                ) : delivery?.intelligentTiering ? (
                  <>Archiving is off, so nothing goes to Glacier.</>
                ) : (
                  <>
                    Archiving is off, so every byte stays in S3 Standard for the whole period. Enable it
                    in Platform Settings → File Archiving and this section shows data migrating to the
                    cheaper tier — and what that saves.
                  </>
                )}
                {' '}GB here is what sits in S3: each upload × its type&apos;s storage factor (
                {(forecast.storageMultipliers as StorageMultiplier[])
                  .filter((m) => m.type !== 'document')
                  .map((m) => `${m.label} × ${num(m.multiplier, 2)}`)
                  .join(', ')}), the original plus what its conversion writes.
              </p>
              <div className={styles.tierGrid}>
                <TierChart
                  title="Data by tier (GB held)"
                  rows={forecast.monthly as ForecastMonth[]}
                  hotOf={(m) => m.storageHotGb}
                  archiveOf={(m) => m.storageArchiveGb}
                  fmt={(v) => `${num(v, v >= 100 ? 0 : 1)} GB`}
                />
                <TierChart
                  title="Storage cost by tier ($ / month)"
                  rows={forecast.monthly as ForecastMonth[]}
                  hotOf={(m) => m.storageHotUsd}
                  archiveOf={(m) => m.storageArchiveUsd}
                  fmt={usd}
                />
              </div>
              <details className={styles.tierDetails}>
                <summary>Show tier table</summary>
                <div className={styles.tableScroll}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th>{delivery?.intelligentTiering ? 'In S3 GB' : 'Standard GB'}</th>
                        {delivery?.intelligentTiering && (
                          <>
                            <th>Frequent GB</th>
                            <th>Infrequent GB</th>
                            <th>Archive Instant GB</th>
                          </>
                        )}
                        <th>Archive GB</th>
                        <th>{delivery?.intelligentTiering ? 'In S3 $' : 'Standard $'}</th>
                        <th>Archive $</th>
                        <th>Saved vs all Standard</th>
                        <th>Storage total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(forecast.monthly as ForecastMonth[]).map((m) => (
                        <tr key={m.month}>
                          <td className={styles.strong}>{m.month}</td>
                          <td>{num(m.storageHotGb)}</td>
                          {delivery?.intelligentTiering && (
                            <>
                              <td>{num(m.storageFrequentGb)}</td>
                              <td>{num(m.storageInfrequentGb)}</td>
                              <td>{num(m.storageInstantGb)}</td>
                            </>
                          )}
                          <td>{num(m.storageArchiveGb)}</td>
                          <td>{usd(m.storageHotUsd)}</td>
                          <td>{usd(m.storageArchiveUsd)}</td>
                          <td>{m.storageSavedUsd > 0 ? usd(m.storageSavedUsd) : '—'}</td>
                          <td className={styles.strong}>{usd(m.storageUsd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </div>

            {/* ── Where it goes ── */}
            <div className={styles.panel}>
              <h3 className={styles.panelTitle}>Breakdown by file type</h3>
              <p className={styles.note}>
                Each conversion step is priced on the lane it would actually run in. A download moves
                the whole file, once per new file:{' '}
                {delivery?.downloadsViaCloudfront
                  ? <>through the {cdn.pricedPlanLabel} CloudFront plan up to {num(delivery.maxCdnDownloadGb, 0)} GB, out of S3 at {usdFine(forecast.rates.egressUsdPerGb)}/GB above that.</>
                  : <>out of S3 at {usdFine(forecast.rates.egressUsdPerGb)}/GB.</>}
                {' '}Viewer opens bill every month on every stored file, falling as it ages, on the
                {' '}{cdn.pricedPlanLabel} CloudFront plan, which serves every viewer file; a document opens
                in the browser&apos;s reader straight from S3, the whole file each time. A step tagged{' '}
                <em>not measured yet</em> is priced from a fallback.
              </p>
              <div className={styles.tableScroll}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Line</th>
                      <th>Jobs</th>
                      <th>Hours</th>
                      <th>Compute</th>
                      <th>Storage growth / mo</th>
                      <th>Storage total</th>
                      <th>Transfer</th>
                      <th>Total</th>
                      <th>Per file</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(forecast.lines as ResultLine[]).map((l) => (
                      <Fragment key={l.id}>
                        <tr>
                          <td className={styles.strong}>
                            {l.label}
                            <span className={styles.sub}>
                              {num(l.files, 0)}/mo × {l.avgSizeMb >= 1024
                                ? `${num(l.avgSizeMb / 1024, 2)} GB`
                                : `${num(l.avgSizeMb, 1)} MB`}
                              {l.conversionsPerFile !== 1 && l.type !== 'document'
                                && ` · ${num(l.conversionsPerFile, 2)}× converted`}
                              {` · stored × ${num(l.storageFactor, 2)}`}
                              {l.viewDelivery === 'cdn' ? ' · views via CloudFront' : l.viewDelivery === 'egress' ? ' · views from S3' : ''}
                              {l.downloadDelivery === 'cdn' ? ' · downloads via CloudFront' : l.downloadDelivery === 'egress' ? ' · downloads from S3' : ''}
                            </span>
                          </td>
                          <td>{num(l.jobs, 0)}</td>
                          <td>{num(l.runHours, 2)}</td>
                          <td>{usd(l.computeUsd)}</td>
                          <td>{usd(l.storageMonthlyUsd)}</td>
                          <td>{usd(l.storageUsd)}</td>
                          <td>
                            {usd(l.transferUsd)}
                            {(l.downloads > 0 || l.views > 0) && (
                              <span className={styles.sub}>
                                {num(l.downloads, 0)} dl · {num(l.views, 0)} views
                              </span>
                            )}
                          </td>
                          <td className={styles.strong}>{usd(l.totalUsd)}</td>
                          <td>{usdFine(l.perFileUsd)}</td>
                        </tr>
                        {l.steps.map((s) => {
                          const fallback = notMeasuredReason(s);
                          return (
                            <tr key={`${l.id}-${s.label}`} className={styles.stepRow}>
                              <td className={styles.stepName}>
                                ↳ {s.label}
                                <span className={styles.laneTag}>{s.lane}</span>
                                {fallback && (
                                  <span className={styles.estTag} title={fallback} aria-label={`Not measured yet: ${fallback}`}>
                                    not measured yet
                                  </span>
                                )}
                              </td>
                              <td>{num(s.jobs, 0)}</td>
                              <td>{num(s.runHours, 2)}</td>
                              <td>{usd(s.computeUsd)}</td>
                              <td colSpan={5} className={styles.muted}>
                                {num(s.hoursPerJob, 2)} h per job
                                {!shared && s.samples > 0 && ` · fitted from ${num(s.samples, 0)} real jobs`}
                                {s.costSource === 'machine'
                                  ? shared ? ' · priced on machine time' : ` · priced on ${num(s.machineHours, 2)} machine-h`
                                  : ' · priced at the per-job lane rate'}
                              </td>
                            </tr>
                          );
                        })}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Provenance (admin only: it shows how much real usage the models rest on) ── */}
            {!shared && (
              <details className={`${styles.panel} ${styles.pricing}`}>
                <summary className={styles.pricingSummary}>How conversions are priced</summary>
                <h4 className={styles.subTitle}>How long we measured each conversion to take</h4>
                <p className={styles.note}>
                  Fitted from finished jobs: fixed overhead per job plus time proportional to input size.
                  A type with no history falls back to a built-in estimate until real jobs land.
                </p>
                <div className={styles.tableScroll}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Lane</th>
                        <th>Jobs measured</th>
                        <th>Typical size</th>
                        <th>Overhead per job</th>
                        <th>Per GB</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(forecast.models as Model[]).length === 0 && (
                        <tr>
                          <td colSpan={6} className={styles.empty}>
                            No finished jobs yet — every line uses the built-in estimates.
                          </td>
                        </tr>
                      )}
                      {(forecast.models as Model[]).map((m) => (
                        <tr key={`${m.key}-${m.lane}`}>
                          <td className={styles.strong}>{m.label}</td>
                          <td>{m.lane}</td>
                          <td>{num(m.samples, 0)}</td>
                          <td>{num(m.medianGb, 2)} GB</td>
                          <td>{num(m.overheadHours * 60, 1)} min</td>
                          <td>{m.hoursPerGb > 0 ? `${num(m.hoursPerGb * 60, 1)} min/GB` : 'size-independent'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <h4 className={styles.subTitle}>What a job costs in machine time</h4>
                <p className={styles.note}>
                  EC2 bills a whole machine from launch to termination. Fitted from machines that have
                  terminated, a job costs the machine time it was busy plus a share of each machine
                  start (boot, image pull, idle tail), split over the jobs one start serves — at least
                  as many as the lane packs on a machine. A lane with fewer than 5 such jobs uses the
                  per-job lane rate instead.
                </p>
                <div className={styles.tableScroll}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Lane</th><th>Jobs measured</th><th>Machines</th><th>Start-up per machine</th>
                        <th>Machine h per run h</th><th>Jobs per start</th><th>Priced</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(forecast.laneModels as LaneModel[]).map((m) => {
                        const measured = m.source === 'measured';
                        return (
                          <tr key={m.lane}>
                            <td className={styles.strong}>{m.lane}</td>
                            <td>{num(m.samples, 0)}</td>
                            <td>{measured ? num(m.machines, 0) : '—'}</td>
                            <td>{measured ? `${num(m.startupHours * 60, 1)} min` : '—'}</td>
                            <td>{measured ? `× ${num(m.busyPerRunHour, 2)}` : '—'}</td>
                            <td>{measured ? num(m.jobsPerStart, 1) : '—'}</td>
                            <td>{measured ? 'machine time' : 'per-job lane rate'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className={styles.note}>
                  Machine prices: heavy {usd(forecast.rates.machineHeavyUsdPerHour)}/h ·
                  standard {usd(forecast.rates.machineStandardUsdPerHour)}/h ·
                  light {usd(forecast.rates.machineLightUsdPerHour)}/h ·
                  GPU {usd(forecast.rates.machineGpuUsdPerHour)}/h.
                  Lane prices: heavy {usd(forecast.rates.laneHeavyUsdPerHour)}/h ·
                  standard {usd(forecast.rates.laneStandardUsdPerHour)}/h ·
                  light {usd(forecast.rates.laneLightUsdPerHour)}/h ·
                  GPU {usd(forecast.rates.laneGpuUsdPerHour)}/h ·
                  S3 {usdFine(forecast.rates.s3UsdPerGbMonth)}/GB-month ·
                  archive {usdFine(forecast.rates.s3ArchiveUsdPerGbMonth)}/GB-month ·
                  egress {usdFine(forecast.rates.egressUsdPerGb)}/GB. Edit them in{' '}
                  <Link to="/admin/costs" className={styles.link}>Cost Analysis → Unit prices</Link>.
                </p>
              </details>
            )}
          </>
        )}

        {!shared && <CalculatorShareLinks />}
      </div>
    </Shell>
  );
};

export default AdminCostCalculator;
